import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';

/* ── helpers ───────────────────────────────────────── */
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const powi = (a, k) => (k >= 0 ? Math.pow(a, k) : 1 / Math.pow(a, -k));

// „log” z ukrytą podstawą 10 (do poleceń, inputów, odpowiedzi)
const logb = (a) => (a === 10 ? '\\log' : `\\log_{${a}}`);
// jawne wypisanie podstawy w wyjaśnieniach (od kroku 2)
const logExp = (a) => (a === 10 ? '\\log_{10}' : `\\log_{${a}}`);

// gcd + fraction utils (do ładnego zapisu b jako liczby / ułamka)
const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function toFraction(x, maxDen = 10000) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  if (!isFinite(x)) return { num: sign, den: 0 };
  let h0 = 0,
    k0 = 1,
    h1 = 1,
    k1 = 0,
    y = x;
  for (let i = 0; i < 20; i++) {
    const a = Math.floor(y);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    if (k2 > maxDen) break;
    h0 = h1;
    k0 = k1;
    h1 = h2;
    k1 = k2;
    const frac = y - a;
    if (frac < 1e-12) break;
    y = 1 / frac;
  }
  let num = h1,
    den = k1;
  if (k0 !== 0 && Math.abs(h0 / k0 - x) < Math.abs(num / den - x)) {
    num = h0;
    den = k0;
  }
  ({ num, den } = simplifyFrac(num * sign, den));
  return { num, den };
}

function niceLatexFromNumber(val) {
  if (!isFinite(val)) return String(val);
  const int = Math.round(val);
  if (Math.abs(val - int) < 1e-12) return String(int);
  let { num, den } = toFraction(val);
  ({ num, den } = simplifyFrac(num, den));
  const sgn = num < 0 ? '-' : '';
  num = Math.abs(num);
  return `${sgn}\\tfrac{${num}}{${den}}`;
}

// b^r zapis bez nawiasów, a nawiasy tylko dla ułamków
function powerLatex(baseLatex, r) {
  const needsParens = /\\dfrac|\\frac|\\tfrac/.test(baseLatex);
  const B = needsParens ? `\\left(${baseLatex}\\right)` : baseLatex;
  return `${B}^{${r}}`;
}

/* ── generator ───────────────────────────────────────
   Zadanie: log_a( b^r ), gdzie b = a^k
   k ∈ {-3,-2,-1,1,2,3}  (prosty rdzeń logarytmu)
   r ∈ {2,3,4,5}         (sensowna potęga do „wyciągnięcia”)
   Wynik: r*k (całkowity)
*/
export function generateTask(options = {}) {
  const a = pick([2, 3, 4, 5, 10]);              // a>0, a≠1
  const k = pick([-3, -2, -1, 1, 2, 3]);         // log_a b = k (bez 0, by nie było trywialnie)
  const r = pick([2, 3, 4, 5]);                  // potęga w argumencie
  const bVal = powi(a, k);
  const bLatex = niceLatexFromNumber(bVal);
  const t = r * k;
  const direction = options.direction === "intoArgument"
    ? "intoArgument"
    : "outOfArgument";
  return {
    type: 'LogPropCoefTask',
    data: { a, r, k, bLatex, t, direction },
  };
}

/* ── render ────────────────────────────────────────── */
export function renderTask({ data }) {
  const { a, r, bLatex, direction } = data;
  const arg = powerLatex(bLatex, r);
  const expression = direction === "intoArgument"
    ? `${r}\\,${logb(a)} ${bLatex}`
    : `${logb(a)} ${arg}`;
  return (
    <div>
      Oblicz wartość wyrażenia:
      <div
        style={{
          marginTop: 14,
          marginBottom: 14,
          fontSize: '16px',
          lineHeight: 1.5,
        }}
      >
        <BlockMath math={expression} />
      </div>
    </div>
  );
}

// input: bez "=", bez labela w podglądzie
export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => {
    onChange && onChange(ans);
  };

  return (
    <div className="space-y-2">
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        label=""   // podgląd pokazuje tylko wpisaną liczbę
      />
    </div>
  );
}

/* ── checking ──────────────────────────────────────── */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object') return false;
  const given = userAnswer.parsed;
  if (given == null) return false;
  return Math.abs(given - data.t) < 1e-4;
}

/* ── answers ──────────────────────────────────────── */
export function renderUserAnswer(userAnswer, task) {
  if (!userAnswer || typeof userAnswer !== 'object' || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

export function renderAnswer({ data }) {
  const { t } = data;
  return <InlineMath math={String(t)} />;
}

/* ── explanation ─────────────────────────────────────
   1) Własność: log_a(b^r) = r·log_a b
   2) Wyciągamy wykładnik i podstawiamy
   3) Obliczamy log_a b = k, więc r·k = t
   4) Wniosek
*/
export function renderExplanation({ data }) {
  const { a, r, bLatex, k, t, direction } = data;
  const arg = powerLatex(bLatex, r);

  if (direction === "intoArgument") {
    return (
      <div className="space-y-2">
        <p><strong>Krok 1:</strong> Przenosimy współczynnik do wykładnika argumentu:</p>
        <BlockMath math={'r\\,\\log_{a}b=\\log_{a}\\left(b^r\\right)'} />

        <p><strong>Krok 2:</strong> Stosujemy własność do danych z zadania:</p>
        <BlockMath
          math={`${r}\\,${logExp(a)}${bLatex}
            =${logExp(a)}\\left(${arg}\\right)`}
        />

        <p><strong>Krok 3:</strong> Obliczamy wartość logarytmu:</p>
        <BlockMath
          math={`${logExp(a)}${bLatex}=${k}
            \\quad\\Rightarrow\\quad
            ${r}\\cdot${k}=${t}`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p><strong>Krok 1:</strong> Własność współczynnika:</p>
      <BlockMath math={'\\log_{a}(b^{r}) = r\\,\\log_{a} b'} />
      {a === 10 && (
        <p style={{ marginTop: 6 }}>
          <em>Uwaga:</em> zapis <InlineMath math={"\\log"} /> oznacza logarytm dziesiętny, czyli{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            <InlineMath math={"\\log x = \\log_{10} x"} />
          </span>.
        </p>
      )}

      <p><strong>Krok 2:</strong> Wyciągamy wykładnik i podstawiamy:</p>
      <BlockMath math={`${logExp(a)} ${arg} = ${r}\\,${logExp(a)} ${bLatex}`} />

      <p><strong>Krok 3:</strong> Obliczamy i mnożymy:</p>
      <BlockMath
        math={`${logExp(a)} ${bLatex} = ${k}\\quad\\Rightarrow\\quad ${r}\\,${logExp(a)} ${bLatex} = ${r}\\cdot${k} = ${t}`}
      />

      <p><strong>Wniosek:</strong></p>
      <BlockMath math={`${logExp(a)} ${arg} = ${t}`} />
    </div>
  );
}
